import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentIcon,
  CheckIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  computeBookingPricing,
  T2B_FEE_RATE,
  T2B_FEE_CAP,
  T2B_FEE_FLOOR,
  VAT_RATE,
} from 'utils/bookingPricing';
import FeeBoundsTooltip from 'components/FeeBoundsTooltip';
import TermsAgreementCheckbox from 'components/TermsAgreementCheckbox';
import { Modal, ModalHead, ModalEyebrow } from 'components/Modal';

const BookingConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedPlace,
  user,
  event,
}) => {
  const { t, i18n } = useTranslation();

  const [documentFiles, setDocumentFiles] = useState({});
  const [docsError, setDocsError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const fileInputs = useRef({});

  const requiredDocs = useMemo(() => {
    const raw = event?.requiredDocuments;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return [];
  }, [event?.requiredDocuments]);

  const pricing = useMemo(
    () => computeBookingPricing(selectedPlace ? selectedPlace.price : 0),
    [selectedPlace]
  );

  const fmtEUR = (n) =>
    new Intl.NumberFormat(i18n?.language || 'pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number.isFinite(n) ? n : 0);

  const placeLabel = useMemo(() => {
    if (!selectedPlace) return '';
    const name = selectedPlace?.name || '';
    const size = selectedPlace?.size ? `${selectedPlace.size} m²` : '';
    return [name, size].filter(Boolean).join(' · ');
  }, [selectedPlace]);

  const uploadedCount = requiredDocs.filter((name) => documentFiles[name])
    .length;

  const allDocsUploaded = uploadedCount === requiredDocs.length;
  const canConfirm = agreedToTerms && allDocsUploaded;

  const handleConfirmClick = () => {
    if (!agreedToTerms) {
      setTermsError(true);
      return;
    }
    if (requiredDocs.length > 0) {
      const missing = requiredDocs.filter((name) => !documentFiles[name]);
      if (missing.length > 0) {
        setDocsError(t('booking.documents_required'));
        return;
      }
      onConfirm?.(documentFiles);
    } else {
      onConfirm?.();
    }
  };

  const brandName = user?.username || user?.name || '—';
  const brandLogo = user?.logo || user?.logoUrl || user?.avatar;
  const eventName = event?.name || '—';

  const pct = (rate) => Math.round(rate * 100);
  // The fee stops tracking the rate at both ends — flat €1 below a €20 stand,
  // flat €50 from €1.000 up. See utils/bookingPricing.js.
  const rawFee = pricing.price * T2B_FEE_RATE;
  const isFeeCapped = pricing.t2bFee < rawFee - 0.005;
  const isFeeFloored = pricing.t2bFee > rawFee + 0.005;

  const title = (
    <>
      {t('booking.confirm_first', 'Confirm your')}{' '}
      <em className="italic">{t('booking.confirm_second', 'request')}</em>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      {/* Mobile — compact sheet header, same shape as the BookingBubble sheet */}
      <div className="lg:hidden px-5 pt-5 pb-4 pr-16 border-b border-line">
        <h2 className="text-xl font-semibold text-ink">{title}</h2>
      </div>
      {/* Desktop — editorial head */}
      <div className="hidden lg:block">
        <ModalHead title={title} size="md" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
        {/* LEFT — identity stack, flow, docs */}
        <div className="lg:overflow-y-auto px-6 sm:px-9 py-7">
          {/* Summary stack — replaces the cramped arrow row */}
          <div className="flex flex-col border border-line rounded-2xl bg-white overflow-hidden">
            {/* Brand */}
            <div className="grid grid-cols-[44px_1fr_auto] items-center gap-4 px-5 py-4 border-b border-line">
              <div className="w-11 h-11 rounded-xl bg-brand-soft grid place-items-center overflow-hidden">
                {brandLogo ? (
                  <img
                    src={brandLogo}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-display font-extrabold text-ink">
                    {brandName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
                  {t('common.brand', 'Brand')}
                </div>
                <div className="text-base font-semibold text-ink leading-tight mt-0.5 truncate">
                  {brandName}
                </div>
              </div>
              <div />
            </div>

            {/* Event */}
            <div className="grid grid-cols-[44px_1fr_auto] items-center gap-4 px-5 py-4 border-b border-line">
              <div className="w-11 h-11 rounded-xl bg-paper-2 grid place-items-center overflow-hidden">
                {event?.avatar || event?.image || event?.coverUrl ? (
                  <img
                    src={event.avatar || event.image || event.coverUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-display font-extrabold text-muted-2">
                    {eventName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
                  {t('common.event', 'Event')}
                </div>
                <div className="text-base font-semibold text-ink leading-tight mt-0.5 truncate">
                  {eventName}
                </div>
              </div>
              <div />
            </div>

            {/* Space */}
            <div className="grid grid-cols-[44px_1fr_auto] items-center gap-4 px-5 py-4">
              <div className="w-11 h-11 rounded-xl bg-paper-2 grid place-items-center">
                <span className="font-display font-extrabold text-ink text-sm">
                  {selectedPlace?.size ? `${selectedPlace.size}m²` : '·'}
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
                  {t('common.space', 'Space')}
                </div>
                <div className="text-base font-semibold text-ink leading-tight mt-0.5 truncate">
                  {placeLabel || '—'}
                </div>
              </div>
              {selectedPlace?.price != null && (
                <div className="text-right font-display font-bold text-[15px] text-ink">
                  {fmtEUR(Number(selectedPlace.price))}
                </div>
              )}
            </div>
          </div>

          {/* Flow strip */}
          <div className="mt-7">
            <div className="flex items-center justify-between mb-3.5">
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.1em] text-ink">
                {t('booking.flow_title', 'How a booking moves')}
              </h3>
              <span className="font-serif italic text-[12.5px] text-muted-2">
                {t('booking.flow_at_step', 'Currently at step {{n}}', { n: 1 })}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 border border-line rounded-2xl bg-white overflow-hidden">
              {[1, 2, 3].map((n) => {
                const isNow = n === 1;
                return (
                  <div
                    key={n}
                    className={`flex flex-col gap-2 px-4 py-4 border-b sm:border-b-0 sm:border-r border-line last:border-b-0 sm:last:border-r-0 ${
                      isNow ? 'bg-brand-soft' : ''
                    }`}
                  >
                    <div className="inline-flex items-center gap-2">
                      <span
                        className={`w-[22px] h-[22px] rounded-full grid place-items-center text-[11px] font-bold border ${
                          isNow
                            ? 'bg-ink text-white border-ink'
                            : 'bg-paper-2 text-muted-2 border-line'
                        }`}
                      >
                        {n}
                      </span>
                      <span
                        className={`font-display text-[11px] font-bold uppercase tracking-[0.1em] ${
                          isNow ? 'text-yellow-04' : 'text-muted'
                        }`}
                      >
                        {t(`booking.flow_step_${n}_label`)}
                      </span>
                    </div>
                    <div className="text-[13.5px] leading-[1.4] text-ink-2">
                      {t(`booking.flow_step_${n}_desc`)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Required documents */}
          {requiredDocs.length > 0 && (
            <div className="mt-7">
              <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3 mb-3.5">
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.1em] text-ink">
                  {t('booking.required_documents')}
                </h3>
                <span className="text-[12.5px] text-muted-2">
                  {t('booking.docs_status', '{{n}} of {{total}} uploaded', {
                    n: uploadedCount,
                    total: requiredDocs.length,
                  })}
                </span>
              </div>
              <p className="text-[13px] text-muted-2 -mt-1.5 mb-3.5">
                {t(
                  'booking.docs_required_note',
                  'All documents below are required. You must upload each one to confirm your request.'
                )}
              </p>
              <div className="flex flex-col gap-2.5">
                {requiredDocs.map((docName, idx) => {
                  const uploaded = documentFiles[docName];
                  return (
                    <div
                      key={idx}
                      className={`grid grid-cols-[34px_minmax(0,1fr)] sm:grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-3 px-4 py-3.5 bg-white border rounded-xl ${
                        uploaded ? 'border-good/40' : 'border-line'
                      }`}
                    >
                      <div
                        className={`w-[34px] h-[34px] rounded-lg grid place-items-center shrink-0 ${
                          uploaded
                            ? 'bg-good-soft text-good'
                            : 'bg-paper-2 text-muted-2'
                        }`}
                      >
                        {uploaded ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <DocumentIcon className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-ink leading-snug m-0 break-words">
                          {docName}
                        </p>
                        <p
                          className={`text-xs leading-relaxed mt-0.5 break-words ${
                            uploaded ? 'text-good' : 'text-muted-2'
                          }`}
                        >
                          {uploaded
                            ? uploaded.name
                            : t(
                                'booking.required_documents_hint',
                                'PDF, JPG, PNG or DOC · max 10 MB'
                              )}
                        </p>
                      </div>
                      <input
                        ref={(el) => {
                          fileInputs.current[docName] = el;
                        }}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setDocumentFiles((prev) => ({
                              ...prev,
                              [docName]: file,
                            }));
                            if (docsError) setDocsError('');
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputs.current[docName]?.click()}
                        className={`col-span-2 sm:col-span-1 w-full sm:w-auto text-[12.5px] font-semibold px-3.5 py-[7px] rounded-full ${
                          uploaded
                            ? 'bg-white text-ink border border-line-strong'
                            : 'bg-brand text-ink hover:bg-brand-hover'
                        }`}
                      >
                        {uploaded
                          ? t('common.replace', 'Replace')
                          : t('common.choose_file', 'Choose file')}
                      </button>
                    </div>
                  );
                })}
              </div>
              {!!docsError && (
                <p className="text-sm text-bad mt-3">{docsError}</p>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — money rail + CTA */}
        <aside className="px-6 sm:px-7 pt-7 pb-0 bg-paper-2 border-t lg:border-t-0 lg:border-l border-line flex flex-col lg:overflow-y-auto">
          <ModalEyebrow>
            {t('booking.what_youll_pay', "What you'll pay")}
          </ModalEyebrow>
          <h3 className="mt-2 mb-4 font-display font-bold text-base text-ink">
            {t('booking.if_approved', 'If the promoter approves')}
          </h3>

          <div className="border border-line rounded-2xl bg-white px-5 sm:px-[22px] pt-[22px] pb-[18px]">
            <div className="flex items-baseline justify-between text-sm text-muted-2 py-1.5">
              <span>
                {t('booking.space_line', 'Space')}
                {selectedPlace?.size ? ` (${selectedPlace.size}m²)` : ''}
              </span>
              <span className="text-ink font-medium">
                {fmtEUR(pricing.price)}
              </span>
            </div>
            <div className="flex items-baseline justify-between text-sm text-muted-2 py-1.5">
              <span>
                {/* Once either bound binds, "(5%)" would be a lie next to the
                    amount actually charged — label the bound instead. */}
                {isFeeCapped &&
                  t('booking.platform_fee_capped', 'Platform fee (max {{cap}})', {
                    cap: fmtEUR(T2B_FEE_CAP),
                  })}
                {isFeeFloored &&
                  t('booking.platform_fee_floored', 'Platform fee (min {{floor}})', {
                    floor: fmtEUR(T2B_FEE_FLOOR),
                  })}
                {!isFeeCapped &&
                  !isFeeFloored &&
                  t('booking.platform_fee', 'Platform fee ({{pct}}%)', {
                    pct: pct(T2B_FEE_RATE),
                  })}
                <FeeBoundsTooltip className="ml-1" />
              </span>
              <span className="text-ink font-medium">
                {fmtEUR(pricing.t2bFee)}
              </span>
            </div>
            <div className="flex items-baseline justify-between text-sm text-muted-2 py-1.5">
              <span>
                {t('booking.vat', 'VAT ({{pct}}%)', { pct: pct(VAT_RATE) })}
              </span>
              <span className="text-ink font-medium">
                {fmtEUR(pricing.vat)}
              </span>
            </div>
            <div className="flex items-baseline justify-between border-t border-line mt-3 pt-3.5">
              <span className="font-display text-sm font-bold uppercase tracking-[0.08em] text-ink">
                {t('booking.total', 'Total')}
              </span>
              <span className="font-display text-[30px] font-bold tracking-[-0.01em] text-ink leading-none">
                {fmtEUR(pricing.total)}
              </span>
            </div>
          </div>

          <div className="mt-4 px-3.5 py-3 rounded-xl bg-paper border border-dashed border-line-strong text-[12.5px] text-muted-2 flex gap-2 items-start">
            <InformationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yellow-04" />
            <span>
              {t(
                'booking.card_not_charged',
                "You won't be charged until the promoter confirms your request. You can cancel any time before then."
              )}
            </span>
          </div>

          <TermsAgreementCheckbox
            className="mt-4"
            checked={agreedToTerms}
            error={termsError}
            onChange={(v) => {
              setAgreedToTerms(v);
              if (v) setTermsError(false);
            }}
          />

          <div className="mt-5 mb-7 flex flex-col gap-2.5">
            <button
              type="button"
              disabled={!canConfirm}
              onClick={handleConfirmClick}
              className="inline-flex justify-center items-center rounded-full
                         bg-brand text-ink font-semibold text-sm px-5 py-4
                         hover:bg-brand-hover transition
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('booking.confirm_request')}
            </button>
            <div className="text-center text-[12.5px] text-muted">
              {t(
                'booking.usual_response',
                'The promoter has 48 hours to respond.'
              )}
            </div>
          </div>
        </aside>
      </div>
    </Modal>
  );
};

export default BookingConfirmationModal;
